import { useListEvents } from '@cocoonspace/sdk-js/domains/events/events'
import { useGetSpace } from '@cocoonspace/sdk-js/domains/spaces/spaces'
import type { Event } from '@cocoonspace/sdk-js/types/event'
import { ArrowRight, HeartCrack } from 'lucide-react'
import { useTranslation } from 'next-i18next'
import dynamic from 'next/dynamic'
import Link from 'next/link'
import { buttonVariants } from '~/components/ui/button'
import { Card } from '~/components/ui/card'
import { Skeleton } from '~/components/ui/skeleton'
import { SpaceCardFooter } from '~/domains/search/components/space-card/space-card-footer'
import { useTracking } from '~/providers/tracking-provider'

const SpaceCardCarousel = dynamic(
	() =>
		import('../../../spaces/components/organisms/space-card-carousel').then(
			(comp) => comp.SpaceCardCarousel,
		),
	{
		ssr: false,
		loading: () => (
			<div className='aspect-[366/175] w-full animate-pulse bg-slate-200'></div>
		),
	},
)

const LastEventSpace = ({ event }: { event: Event }) => {
	const { t } = useTranslation()
	const { track } = useTracking()

	const { data: space, isLoading } = useGetSpace(event.space?.id!)

	return (
		<>
			<div className='md:w-1/2'>
				<div className='overflow-hidden rounded'>
					{isLoading ? (
						<Skeleton className='aspect-[366/175]' />
					) : (
						<SpaceCardCarousel space={space?.data!} />
					)}
				</div>
			</div>

			<div className='flex flex-col md:w-1/2'>
				{isLoading ? (
					<SpaceCardFooter.skeleton />
				) : (
					<SpaceCardFooter
						space={space?.data!}
						className='pt-0'
					/>
				)}

				<div className='mt-auto text-right'>
					{isLoading ? (
						<Skeleton className='ml-auto h-[14px] w-1/2' />
					) : (
						<Link
							className={buttonVariants({ variant: 'textPrimary' })}
							href={`/${space?.data.zone}/${space?.data.slug}`}
							onClick={() => track.click('dashboard-bookAgain-link')}
						>
							{t('cwDashboard:lastEvent.linkLabel')}

							<ArrowRight
								size={20}
								className='ml-2 text-primary'
							/>
						</Link>
					)}
				</div>
			</div>
		</>
	)
}

const NoPastEvent = () => {
	const { t } = useTranslation()

	return (
		<div className='flex min-h-[150px] w-full flex-col'>
			<div className='flex flex-1 flex-col items-center justify-center py-4 text-center text-foreground/50 text-lg'>
				<HeartCrack />

				{t('cwDashboard:lastEvent.noEvent')}
			</div>

			<div className='mt-auto text-right'>
				<Link
					className={buttonVariants({ variant: 'textPrimary' })}
					href={`/paris`}
				>
					{t('cwDashboard:lastEvent.noEventlinkLabel')}

					<ArrowRight
						size={20}
						className='ml-2 text-primary'
					/>
				</Link>
			</div>
		</div>
	)
}

const LoadingView = () => {
	return (
		<>
			<Skeleton className='h-[18px] w-[200px]' />

			<div className='flex flex-col gap-2 md:flex-row'>
				<div className='md:w-1/2'>
					<div className='overflow-hidden rounded'>
						<Skeleton className='aspect-[366/175]' />
					</div>
				</div>

				<div className='flex flex-col md:w-1/2'>
					<SpaceCardFooter.skeleton />

					<div className='mt-auto text-right'>
						<Skeleton className='ml-auto h-[14px] w-1/2' />
					</div>
				</div>
			</div>
		</>
	)
}

const ContentView = ({ pastEvents }: { pastEvents: Event[] }) => {
	const { t } = useTranslation()

	return (
		<>
			<div className='font-bold'>{t('cwDashboard:lastEvent.title')}</div>
			<div className='flex flex-col gap-2 md:flex-row'>
				{pastEvents?.length > 0 ? (
					<LastEventSpace event={pastEvents[0]} />
				) : (
					<NoPastEvent />
				)}
			</div>
		</>
	)
}

export const DashboardLastEventCard = () => {
	const { data: pastEvents, isLoading } = useListEvents({
		include: 'past',
		limit: 1,
	})

	return (
		<div className='md:col-span-2'>
			<Card className='flex flex-col gap-y-4 p-6'>
				{isLoading ? (
					<LoadingView />
				) : (
					<ContentView pastEvents={pastEvents?.data ?? []} />
				)}
			</Card>
		</div>
	)
}
